/**
 * Unit table / display settings — constants and panel-settings parsers.
 *
 * These utilities are shared across all catalog view modes (List, Grid, Cards).
 * "Active columns" in List = columns in the table.
 * "Active columns" in Grid / Cards = which product fields are rendered on each tile / card.
 */

import { AVAILABILITY_STATUS } from '../../../shared/constants/availabilityStatus.js'
import { getProductAvailabilityState, isProductAccessible } from '../../../shared/utils/productUtils.js'
import { VIEW_MODE } from './viewModes.js'

// ── Constants ──────────────────────────────────────────────────────────────────

/**
 * Canonical IDs for unit action buttons, used in `units_table_actions` panelSettings
 * and in per-view action registries. Any comparison against action entry `.id`
 * must use these constants — never a bare string literal.
 *
 * @readonly
 * @enum {string}
 */
export const ACTION_IDS = /** @type {const} */ ({
  FAVORITES:        'favorites',
  FLOOR_PLAN:       'floor_plan',
  RATE_CALCULATOR:  'rate_calculator',
})

const VALID_VIEW_MODES = new Set(Object.values(VIEW_MODE))
const DEFAULT_LOAD_CONFIG = {
  load_with_view_desktop: VIEW_MODE.LIST,
  load_with_view_mobile: VIEW_MODE.LIST,
}

// ── Parsers ────────────────────────────────────────────────────────────────────

/**
 * Fallback active columns used when `units_table_active_columns` is absent from panelSettings.
 * Covers the union of all column IDs across List, Cards, and Tiles views.
 * Order mirrors the natural display order for each view.
 */
const DEFAULT_ACTIVE_COLUMNS = [
  { id: 'id_cad',      is_active: true, order: 1 },
  { id: 'building_id', is_active: true, order: 2 },
  { id: 'gate',        is_active: true, order: 3 },
  { id: 'ref_tier',    is_active: true, order: 4 },
  { id: 'rooms',       is_active: true, order: 5 },
  { id: 'area',        is_active: true, order: 6 },
  { id: 'status',      is_active: true, order: 7 },
  { id: 'price',       is_active: true, order: 8 },
  { id: 'actions',     is_active: true, order: 9 },
]

/**
 * Normalizes `units_table_active_columns` from panelSettings.
 *
 * Expected format: `[{ id: string, is_active: boolean, order: number }]`
 * Missing key / non-array → falls back to `DEFAULT_ACTIVE_COLUMNS` (all standard
 * columns active). This ensures all views render correctly without panel settings.
 *
 * Shared across view modes:
 * - List  → which table columns to render
 * - Grid  → which data fields to show on a product tile
 * - Cards → which data fields to show on a product card
 *
 * @param {object} panelSettings
 * @returns {{ activeMap: Record<string, boolean>, orderedIds: string[] }}
 *   `activeMap`  — O(1) lookup: `activeMap['rooms'] === true`
 *   `orderedIds` — active column ids only, sorted ascending by `order`
 */
export function parseActiveColumns(panelSettings) {
  const raw = panelSettings?.units_table_active_columns

  const source = Array.isArray(raw) ? raw : DEFAULT_ACTIVE_COLUMNS

  const sorted = source
    .filter(entry => entry.is_active)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))

  return {
    activeMap:  Object.fromEntries(sorted.map(e => [e.id, true])),
    orderedIds: sorted.map(e => e.id),
  }
}

/**
 * Normalizes `units_table_actions` from panelSettings.
 * Returns only active action entries, sorted ascending by `order`.
 * Falls back to `[{ id: ACTION_IDS.FLOOR_PLAN, is_active: true, order: 1 }]` when
 * the key is absent or empty — floor_plan is always the minimum sensible default.
 *
 * Each entry may carry an optional `behavior` field ('internal' | 'external' | …)
 * reserved for V2 configurable action routing — ignored in V1.
 *
 * @param {object} panelSettings
 * @returns {Array<{ id: string, is_active: boolean, order: number, behavior?: string }>}
 */
export function parseActiveActions(panelSettings) {
  const DEFAULT = [{ id: ACTION_IDS.FLOOR_PLAN, is_active: true, order: 1 }]
  const raw = panelSettings?.units_table_actions

  if (!Array.isArray(raw)) return DEFAULT

  const active = raw
    .filter(entry => entry.is_active)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))

  return active.length > 0 ? active : DEFAULT
}

/**
 * Resolves the initial catalog view mode from panel settings.
 * TODO: remove this util when initialView got from contextCapabilities
 *
 * @param {object|null|undefined} panelSettings
 * @param {boolean} isMobile
 * @returns {string}
 */
export function parseInitialViewMode(panelSettings, isMobile) {
  const raw = panelSettings?.units_table_load_config
  const candidate = isMobile
    ? raw?.load_with_view_mobile
    : raw?.load_with_view_desktop

  if (typeof candidate === 'string' && VALID_VIEW_MODES.has(candidate)) {
    return candidate
  }

  return isMobile
    ? DEFAULT_LOAD_CONFIG.load_with_view_mobile
    : DEFAULT_LOAD_CONFIG.load_with_view_desktop
}

// ── Row state ──────────────────────────────────────────────────────────────────

/**
 * Returns a `getRowState` function closed over the project's accessibility settings.
 *
 * The resulting function maps a product object to one of the RecordTable row states:
 * - `'inaccessible'` — sold/reserved AND the corresponding `is_accessible` is false
 *                      → row is visually dimmed and non-interactive (CSS handles it)
 * - `'sold'`         — sold AND accessible
 * - `'reserved'`     — reserved AND accessible  (same CSS as sold, different semantic)
 * - `null`           — available or unknown → normal row
 *
 * Usage (in CatalogView or any view that creates the RecordTable):
 * ```js
 * const getRowState = makeGetRowState(panelSettings?.unit_sheet_accessibility)
 * <ProductList getRowState={getRowState} … />
 * ```
 *
 * @param {object|null|undefined} accessibility  — `panelSettings.unit_sheet_accessibility`
 * @returns {(product: object) => 'inaccessible'|'sold'|'reserved'|null}
 */
export { getProductAvailabilityState }

/**
 * Returns whether a product should remain in the publicly navigable catalog.
 * Available / unknown units are always kept; sold / reserved units are kept only
 * when their `is_accessible` flag is enabled in panel settings.
 *
 * @param {object} product
 * @param {object|null|undefined} accessibility
 * @returns {boolean}
 */
export { isProductAccessible }

export function makeGetRowState(accessibility) {
  return function getRowState(p) {
    return getProductAvailabilityState(p, accessibility)
  }
}

// ── Plan visibility ────────────────────────────────────────────────────────────────

/**
 * Returns a `getPlanVisible` function closed over the project's accessibility settings.
 *
 * Determines whether the floor plan thumbnail should be shown for a given product.
 * When the CMS operator marks `plan_visible: false` for sold or reserved units the
 * thumbnail is replaced by a placeholder so sensitive layout information isn't
 * revealed to the public. Available units always show the plan.
 *
 * Usage:
 * ```js
 * const getPlanVisible = makePlanVisible(panelSettings?.unit_sheet_accessibility)
 * <UnitPlanThumb planVisible={getPlanVisible(p)} … />
 * ```
 *
 * @param {object|null|undefined} accessibility  — `panelSettings.unit_sheet_accessibility`
 * @returns {(product: object) => boolean}
 */
export function makePlanVisible(accessibility) {
  return function getPlanVisible(p) {
    if (p.status === AVAILABILITY_STATUS.SOLD) {
      return accessibility?.sold_units?.plan_visible ?? true
    }
    if (p.status === AVAILABILITY_STATUS.RESERVED) {
      return accessibility?.reserved_units?.plan_visible ?? true
    }
    return true
  }
}
